import type { TriggerType } from '@atlaskit/editor-common/analytics';

/**
 * The plugin state is a discriminated union representing the lifecycle
 * of an AI inline completion suggestion.
 *
 * Trigger mode:
 *   • 'manual' — user presses Cmd+Shift+Space
 *     (only when the document meets the minimum word count: default 400, or 200/300/350 via
 *     `platform_editor_ai_autocomplete_ux_config` tokens `word-count-200`, `word-count-300`,
 *     `word-count-350` in the config string list).
 *     Suggestion shows immediately; Tab accepts.
 *   • 'auto' — user completes the trigger word (e.g. Summary) in a heading or in the first four
 *     document lines (block boundaries count as newlines), with the same minimum word count as manual.
 *     API runs; if the response arrives before Enter, the ghost stays hidden until Enter (then it
 *     shows at the new cursor). Edits on the same line as the trigger are allowed while loading
 *     or pending; after the cursor moves to a lower line, typing discards the session (Escape /
 *     blur still dismiss). If Enter happens before the response returns and the doc is unchanged
 *     since that Enter, the ghost shows when the response arrives; otherwise that response is
 *     discarded. Tab accepts.
 *
 * State transitions:
 *   idle
 *     → TRIGGER_MANUAL / TRIGGER_AUTO                    → loading
 *   loading
 *     → SET_SUGGESTION (manual)                          → suggestion
 *     → SET_SUGGESTION (auto)                            → suggestion_pending
 *     → SET_SUGGESTION (auto, user pressed Enter in load) → suggestion (visible at current cursor)
 *     → DISMISS                                          → idle
 *   suggestion_pending (auto only)
 *     → REVEAL_SUGGESTION (after Enter; uses cursor on next line) → suggestion
 *     → DISMISS                                          → idle
 *   suggestion
 *     → accept (Tab) / DISMISS                           → idle
 */

export type SuggestionTrigger = 'manual' | 'auto';

export type AiAutocompletePluginState =
	| { status: 'idle' }
	| {
			cursorPos: number;
			status: 'loading';
			trigger: SuggestionTrigger;
	  }
	| {
			cursorPos: number;
			status: 'suggestion_pending';
			suggestion: string;
			trigger: 'auto';
	  }
	| {
			cursorPos: number;
			status: 'suggestion';
			suggestion: string;
			trigger: SuggestionTrigger;
	  };

// ---------------------------------------------------------------------------
// Actions dispatched via tr.setMeta(aiAutocompletePluginKey, action).
// ---------------------------------------------------------------------------

export const ACTIONS = {
	TRIGGER_MANUAL: 'TRIGGER_MANUAL',
	TRIGGER_AUTO: 'TRIGGER_AUTO',
	SET_SUGGESTION: 'SET_SUGGESTION',
	REVEAL_SUGGESTION: 'REVEAL_SUGGESTION',
	DISMISS: 'DISMISS',
} as const;

export type AiAutocompletePluginAction =
	| {
			cursorPos: number;
			triggerType?: TriggerType;
			type: typeof ACTIONS.TRIGGER_AUTO;
	  }
	| {
			cursorPos: number;
			triggerType?: TriggerType;
			type: typeof ACTIONS.TRIGGER_MANUAL;
	  }
	| {
			displaySuggestionImmediately?: boolean;
			suggestion: string;
			suggestionCursorPos?: number;
			type: typeof ACTIONS.SET_SUGGESTION;
	  }
	| { cursorPos?: number; type: typeof ACTIONS.REVEAL_SUGGESTION }
	| { type: typeof ACTIONS.DISMISS };
